/**
 * Sortable lists in Teslyn Cloud (MB's R11, as Teslyn Field's SortableTable): a click on a column heading sorts by
 * it, with its arrow; a second click turns the order round; the order is kept per page in this browser. The rules
 * are `@teslyn/domain`'s (`table-sort.ts`).
 */
import { useState, type ReactElement, type ReactNode } from 'react';
import {
  nextSort,
  parseSortState,
  sortKey,
  sortRows,
  type SortDir,
  type SortState,
  type SortValue,
} from '@teslyn/domain';
import { Icon } from './Icon.tsx';

export interface SortColumn<T> {
  readonly id: string;
  /** The heading's words. */
  readonly label: ReactNode;
  readonly value: (row: T) => SortValue;
  /** The order of a first click (dates and numbers often read best newest or largest first). */
  readonly firstDir?: SortDir;
  /** The heading cell's class, for example `num` for a right-aligned figure. */
  readonly className?: string;
  /** Hover text saying what the column counts. */
  readonly title?: string;
}

function readSort(key: string, columns: readonly string[], fallback: SortState): SortState {
  try {
    return parseSortState(window.localStorage.getItem(key), columns, fallback);
  } catch {
    return fallback;
  }
}

/** The rows in the page's order, the order and the heading click. */
export function useSortableRows<T>(
  page: string,
  rows: readonly T[],
  columns: readonly SortColumn<T>[],
  initial: SortState,
): { readonly rows: T[]; readonly sort: SortState; readonly onSort: (id: string) => void } {
  const key = sortKey('cloud', page);
  const [sort, setSort] = useState(() =>
    readSort(
      key,
      columns.map((c) => c.id),
      initial,
    ),
  );
  const column = columns.find((c) => c.id === sort.column) ?? columns[0]!;
  function onSort(id: string): void {
    const next = nextSort(sort, id, columns.find((c) => c.id === id)?.firstDir);
    setSort(next);
    try {
      window.localStorage.setItem(key, JSON.stringify(next));
    } catch {
      // storage blocked: the order holds while the page is open
    }
  }
  return { rows: sortRows(rows, column.value, sort.dir), sort, onSort };
}

/** A sortable column heading: a button with the words and the arrow of the order it sorts by. */
export function SortHeader<T>({
  column,
  sort,
  onSort,
  rowSpan,
}: {
  readonly column: SortColumn<T>;
  readonly sort: SortState;
  readonly onSort: (id: string) => void;
  /** A heading beside a group of columns spans the group's two header rows. */
  readonly rowSpan?: number;
}): ReactElement {
  const on = sort.column === column.id;
  return (
    <th
      scope="col"
      rowSpan={rowSpan}
      className={['label', column.className].filter(Boolean).join(' ')}
      aria-sort={on ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      title={column.title}
    >
      <button type="button" className="sort-head" onClick={() => onSort(column.id)}>
        {column.label}
        {on && (
          <Icon
            name={sort.dir === 'asc' ? 'arrow-up' : 'arrow-down'}
            className={`sort-arrow sort-arrow--${sort.dir}`}
          />
        )}
      </button>
    </th>
  );
}
